import { useEffect, useState } from "react";
import { api } from "../api/client";
import { wsClient } from "../api/ws";
import type { OrchestratorAutopilotLogEntry } from "../api/client";

interface ServerSnapshot {
  name: string;
  alias: string;
  host: string;
  reachable: boolean;
  hostname?: string;
  uptime_s?: number;
  load_avg?: [number, number, number];
  cpu_cores?: number;
  mem_total_mb?: number;
  mem_used_mb?: number;
  disk_total_gb?: number;
  disk_used_gb?: number;
  disk_used_pct?: number;
  containers?: Array<{ name: string; status: string; ports?: string }>;
  error?: string;
}

interface EnvSnapshot {
  url: string;
  reachable: boolean;
  status?: string;
  git_sha?: string;
  branch?: string;
  migrations_applied?: number;
  migrations_pending?: number;
  checks?: Record<string, string>;
}

interface CoagentSnapshot {
  prod: EnvSnapshot;
  stage: EnvSnapshot;
}

function fmtGb(gb?: number): string {
  if (typeof gb !== "number") return "—";
  return `${gb.toFixed(1)} GB`;
}
function fmtMb(mb?: number): string {
  if (typeof mb !== "number") return "—";
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
}
function fmtUptime(s?: number): string {
  if (!s) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return d > 0 ? `${d}д ${h}ч` : `${h}ч`;
}

interface FreezeStatus {
  frozen: boolean;
  last_change_at: string | null;
  last_change_by: string | null;
}

export function Dashboard() {
  const [servers, setServers] = useState<ServerSnapshot[] | null>(null);
  const [coagent, setCoagent] = useState<CoagentSnapshot | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [orchConfig, setOrchConfig] = useState<Record<string, unknown>>({});
  const [autopilotLog, setAutopilotLog] = useState<OrchestratorAutopilotLogEntry[]>([]);
  const [orchToggling, setOrchToggling] = useState(false);
  const [freeze, setFreeze] = useState<FreezeStatus | null>(null);
  const [freezeBusy, setFreezeBusy] = useState(false);

  const loadOrchestrator = () => {
    api.orchestrator.config().then((r) => setOrchConfig(r.config)).catch(() => {});
    api.orchestrator
      .autopilotLog(10)
      .then((r) => setAutopilotLog(r.log))
      .catch(() => {});
    api.autopilotFreeze.status().then(setFreeze).catch(() => {});
  };

  useEffect(() => {
    let canceled = false;
    const load = () => {
      api
        .dashboard()
        .then((d) => {
          if (canceled) return;
          setServers(d.servers as ServerSnapshot[]);
          setCoagent(d.coagent as CoagentSnapshot);
          setErr(null);
        })
        .catch((e) => !canceled && setErr(String(e)));
    };
    load();
    loadOrchestrator();
    const t = setInterval(() => {
      load();
      loadOrchestrator();
    }, 30_000);
    const unsub = wsClient.subscribe((e) => {
      if (e.type === "dashboard.update") load();
      if (
        e.type === "autopilot.tick" &&
        ((e.payload as { source?: string })?.source === "jarvis-config" ||
          (e.payload as { source?: string })?.source === "jarvis" ||
          (e.payload as { source?: string })?.source === "autopilot-freeze")
      ) {
        loadOrchestrator();
      }
    });
    return () => {
      canceled = true;
      clearInterval(t);
      unsub();
    };
  }, []);

  const autopilotMode = (orchConfig.autopilot_mode as string) ?? "off";
  const balance = Number(orchConfig.priority_balance ?? 50);
  const intervalSec = Number(orchConfig.autopilot_interval_sec ?? 600);
  const todayDispatched = autopilotLog
    .filter((r) => {
      const d = new Date(r.created_at);
      const today = new Date();
      return (
        d.getUTCFullYear() === today.getUTCFullYear() &&
        d.getUTCMonth() === today.getUTCMonth() &&
        d.getUTCDate() === today.getUTCDate()
      );
    })
    .reduce((acc, r) => acc + (r.dispatched_ids?.length ?? 0), 0);
  const lastCycle = autopilotLog[0];

  const toggleAutopilot = async () => {
    if (orchToggling) return;
    setOrchToggling(true);
    try {
      const next = autopilotMode === "on" ? "off" : "on";
      const r = await api.orchestrator.setConfig("autopilot_mode", next);
      setOrchConfig(r.config);
    } catch (e) {
      alert("Не удалось переключить: " + String(e));
    } finally {
      setOrchToggling(false);
    }
  };

  const toggleFreeze = async () => {
    if (freezeBusy) return;
    const isFrozen = freeze?.frozen ?? false;
    const msg = isFrozen
      ? "Разморозить все автопилоты? Они возобновят работу в следующем цикле."
      : "ЗАМОРОЗИТЬ все автопилоты? Все демоны пропустят следующие циклы пока не разморозите.";
    if (!window.confirm(msg)) return;
    setFreezeBusy(true);
    try {
      const r = isFrozen
        ? await api.autopilotFreeze.unfreeze()
        : await api.autopilotFreeze.freeze();
      setFreeze({
        frozen: r.frozen,
        last_change_at: r.last_change_at,
        last_change_by: r.last_change_by,
      });
    } catch (e) {
      alert("Не удалось переключить freeze: " + String(e));
    } finally {
      setFreezeBusy(false);
    }
  };

  const freezeAgeText = (): string => {
    if (!freeze?.last_change_at) return "";
    const delta = Math.max(0, Date.now() - Date.parse(freeze.last_change_at));
    const mins = Math.floor(delta / 60_000);
    if (mins < 1) return "только что";
    if (mins < 60) return `${mins} мин назад`;
    const hrs = Math.floor(mins / 60);
    return `${hrs} ч назад`;
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold">Обзор</h1>
        <p className="text-sm text-panel-muted">Состояние серверов и платформы, обновление каждые 30 сек.</p>
      </header>

      {err && <div className="card pill-err border-panel-err">Ошибка: {err}</div>}

      <section>
        <div
          className={
            "card flex items-center justify-between p-6 " +
            (autopilotMode === "on"
              ? "border-panel-accent bg-panel-accent/10"
              : "border-panel-border")
          }
        >
          <div>
            <div className="text-xs uppercase text-panel-muted">Режим Jarvis</div>
            <div className="text-2xl font-semibold mt-1">
              {autopilotMode === "on" ? "🤖 АВТОПИЛОТ ВКЛ" : "⏸ Ручной режим"}
            </div>
            <div className="text-sm text-panel-muted mt-1">
              {autopilotMode === "on"
                ? `Цикл каждые ${intervalSec}s · приоритет ${balance}/100 · сегодня диспатчено: ${todayDispatched}`
                : `Жмёт кнопку — Jarvis начнёт сам разбирать Inbox · приоритет ${balance}/100`}
            </div>
            {lastCycle && (
              <div className="text-[11px] text-panel-muted mt-1">
                Последний цикл: {new Date(lastCycle.created_at).toLocaleString()} ·
                {" "}{lastCycle.triaged_count} взято · {lastCycle.dispatched_ids?.length ?? 0} ушло в работу
              </div>
            )}
          </div>
          <button
            onClick={toggleAutopilot}
            disabled={orchToggling}
            className={"btn " + (autopilotMode === "on" ? "" : "btn-primary")}
          >
            {orchToggling ? "…" : autopilotMode === "on" ? "Выключить" : "Включить"}
          </button>
        </div>
      </section>

      {/* Emergency Stop — kill switch for all autopilot daemons */}
      <section>
        <div
          className={
            "card flex items-center justify-between p-6 " +
            (freeze?.frozen
              ? "border-panel-err bg-panel-err/10"
              : "border-panel-border")
          }
        >
          <div>
            <div className="text-xs uppercase text-panel-muted">🛑 Emergency Stop</div>
            <div className="text-2xl font-semibold mt-1">
              {freeze?.frozen
                ? "❄️ FROZEN"
                : "🟢 All systems operational"}
            </div>
            <div className="text-sm text-panel-muted mt-1">
              {freeze?.frozen
                ? `Все автопилоты заморожены — ${freezeAgeText()}${freeze?.last_change_by ? ` · by ${freeze.last_change_by}` : ""}`
                : "Все демоны крутятся. Нажми чтобы немедленно остановить все автопилоты."}
            </div>
          </div>
          <button
            onClick={toggleFreeze}
            disabled={freezeBusy}
            className={
              "btn " +
              (freeze?.frozen
                ? "border-panel-ok text-panel-ok"
                : "border-panel-err text-panel-err hover:bg-panel-err hover:text-black")
            }
          >
            {freezeBusy
              ? "…"
              : freeze?.frozen
                ? "UNFREEZE"
                : "FREEZE ALL AUTOPILOTS"}
          </button>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-medium text-panel-muted">Серверы</h2>
        <div className="grid gap-3 md:grid-cols-3">
          {(servers ?? []).map((s) => (
            <div key={s.alias} className="card">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <div className="font-medium">{s.alias}</div>
                  <div className="text-xs text-panel-muted">{s.hostname ?? s.host}</div>
                </div>
                <span className={`pill ${s.reachable ? "pill-ok" : "pill-err"}`}>
                  {s.reachable ? "online" : "offline"}
                </span>
              </div>
              {s.error && <div className="text-xs text-panel-err">{s.error}</div>}
              {s.reachable && (
                <dl className="grid grid-cols-2 gap-1 text-xs">
                  <dt className="text-panel-muted">CPU</dt>
                  <dd>
                    {(s.load_avg && s.load_avg[0] != null) ? s.load_avg[0].toFixed(2) : "—"} / {s.cpu_cores ?? "—"} cores
                  </dd>
                  <dt className="text-panel-muted">RAM</dt>
                  <dd>
                    {fmtMb(s.mem_used_mb)} / {fmtMb(s.mem_total_mb)}
                  </dd>
                  <dt className="text-panel-muted">Disk</dt>
                  <dd>
                    {fmtGb(s.disk_used_gb)} / {fmtGb(s.disk_total_gb)} ({s.disk_used_pct?.toFixed(0) ?? "—"}%)
                  </dd>
                  <dt className="text-panel-muted">Uptime</dt>
                  <dd>{fmtUptime(s.uptime_s)}</dd>
                  <dt className="text-panel-muted">Containers</dt>
                  <dd>{s.containers?.length ?? 0}</dd>
                </dl>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-medium text-panel-muted">Платформа Coagent</h2>
        {coagent && (
          <div className="grid gap-3 md:grid-cols-2">
            {(["prod", "stage"] as const).map((env) => {
              const e = coagent[env];
              return (
                <div key={env} className="card">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="font-medium">{env === "prod" ? "Production" : "Staging"}</div>
                    <span className={`pill ${e.reachable ? "pill-ok" : "pill-err"}`}>
                      {e.reachable ? e.status ?? "ok" : "unreachable"}
                    </span>
                  </div>
                  {e.reachable && (
                    <dl className="grid grid-cols-2 gap-1 text-xs">
                      <dt className="text-panel-muted">Branch</dt>
                      <dd>{e.branch ?? "—"}</dd>
                      <dt className="text-panel-muted">SHA</dt>
                      <dd>{e.git_sha?.slice(0, 10) ?? "—"}</dd>
                      <dt className="text-panel-muted">Migrations</dt>
                      <dd>
                        {e.migrations_applied ?? "—"} / {e.migrations_pending ?? 0} pending
                      </dd>
                      <dt className="text-panel-muted">URL</dt>
                      <dd className="break-all">
                        <code className="text-[10px]">{e.url}</code>
                      </dd>
                    </dl>
                  )}
                  {e.checks && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(e.checks).map(([k, v]) => (
                        <span key={k} className={`pill ${v === "ok" ? "pill-ok" : "pill-warn"}`}>
                          {k}: {v}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
